import { throwError } from 'rxjs'
import { ajax } from 'rxjs/ajax'
import { catchError } from 'rxjs/operators'
import { env } from 'shared/env'

const baseURL = env.SERVER_ADDR
const requestHeader = (isFile = false) => {
  const headers = {}
  if (!isFile) {
    headers['Content-Type'] = 'application/json'
  }
  const userData = JSON.parse(localStorage.getItem('user_info') || '{}')
  let token: string | boolean = false
  if (userData.token) {
    token = userData.token
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  return headers
}

const objToQueryString = (obj) => {
  return Object.keys(obj)
    .map((key) => key + '=' + obj[key])
    .join('&')
}

const handleError = (operation: string) => (err: any) => {
  const errMsg = `Error in ${operation}()`

  if (err.status === 401) {
    localStorage.clear()
    window.location.href = '/auth/login'
  }
  return throwError(() => console.error(errMsg))
}

export const getSuppliers = () =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/suppliers`,
  }).pipe(catchError(handleError('getSuppliers')))

export const getPurchases = (filters) =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/purchases?${objToQueryString(filters)}`,
  }).pipe(catchError(handleError('getPurchases')))

export const getPurchaseDetails = (purchase_id) =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/purchases/${purchase_id}`,
  }).pipe(catchError(handleError('getPurchaseDetails')))

export const deletePurchase = (purchase_id) =>
  ajax({
    headers: requestHeader(),
    method: 'DELETE',
    withCredentials: false,
    url: `${baseURL}api/v2/purchases/${purchase_id}`,
  }).pipe(catchError(handleError('deletePurchase')))

export const getCategories = (storeFilter = null) =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/products/collections?store_id=${storeFilter}`,
  }).pipe(catchError(handleError('getCategories')))

export const getCatalogue = (value, catId) =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/purchases/catalogue?search=${value}&catId=${
      catId || null
    }`,
  }).pipe(catchError(handleError('getCatalogue')))

export const createCategory = (data: any) =>
  ajax({
    headers: requestHeader(),
    method: 'POST',
    withCredentials: false,
    url: `${baseURL}api/v2/products/categories`,
    body: data,
  }).pipe(catchError(handleError('createCategory')))

export const updateInventoryCategory = (data: any) =>
  ajax({
    headers: requestHeader(),
    method: 'PUT',
    withCredentials: false,
    url: `${baseURL}api/v2/products/categories`,
    body: data,
  }).pipe(catchError(handleError('updateInventoryCategory')))
export const getProducts = (filters) => {
  const queryParams = new URLSearchParams({
    page: filters.page || 0,
    page_size: filters.page_size || 50,
    search: filters.search || '',
  })

  if (filters.collection_id) {
    queryParams.append('collection_id', filters.collection_id)
  }

  if (filters.status && filters.status !== 'all') {
    queryParams.append('status', filters.status)
  }

  if (filters.store_filter && filters.store_filter !== 'all') {
    if (filters.store_filter === 'none') {
      queryParams.append('has_links', 'false')
    } else {
      queryParams.append('store_id', filters.store_filter)
    }
  }

  return ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/products?${queryParams.toString()}`,
  }).pipe(catchError(handleError('getProducts')))
}

export const getWarehouseInventory = (filters) => {
  const url = `${baseURL}api/v2/inventory/warehouse/${
    filters.warehouse_id
  }?sortKey=${filters.sortKey || 'available'}&sortOrder=${
    filters.sortOrder || 'desc'
  }&warehouse=${filters.warehouse_id || null}&page=${
    filters.page || 0
  }&search=${filters.search || ''}&page_size=${filters.page_size || null}${
    filters.searchBySKU ? '&searchBySKU=1' : ''
  }`

  return ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url,
  }).pipe(catchError(handleError('getWarehouseInventory')))
}

export const getInventoryItems = (product_id) =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/product/${product_id}/inventory`,
  }).pipe(catchError(handleError('getInventoryItems')))

export const getInventoryUploads = () =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/products/upload`,
  }).pipe(catchError(handleError('getInventoryUploads')))

export const getProductMeta = () =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/products/meta`,
  }).pipe(catchError(handleError('getProductMeta')))

export const updateStockItemStatus = (data: any) =>
  ajax({
    headers: requestHeader(),
    method: 'POST',
    withCredentials: false,
    url: `${baseURL}api/v2/product/inventory/items`,
    body: data,
  }).pipe(catchError(handleError('updateStockItemStatus')))

export const createPurchaseOrder = (data: any) =>
  ajax({
    headers: requestHeader(),
    method: 'POST',
    withCredentials: false,
    url: `${baseURL}api/v2/purchases`,
    body: data,
  }).pipe(catchError(handleError('createPurchaseOrder')))

export const updateInventoryCount = (data: any) =>
  ajax({
    headers: requestHeader(),
    method: 'PUT',
    withCredentials: false,
    url: `${baseURL}api/v2/product/inventory`,
    body: data,
  }).pipe(catchError(handleError('updateInventoryCount')))

interface IUpdateInventory {
  count: string
  status: string
  update_type: string
  notes: string
}

export const updateInventory = (product_id, wh_id, data: IUpdateInventory) =>
  ajax({
    headers: requestHeader(),
    method: 'PUT',
    withCredentials: false,
    url: `${baseURL}api/v2/inventory/${product_id}/warehouse/${wh_id}`,
    body: data,
  }).pipe(catchError(handleError('updateInventoryCount')))

export const getGroupedWarehouseInventory = (filters: {
  warehouse_id: number | string
  page?: number
  page_size?: number
  search?: string
  searchBySKU?: boolean
}) => {
  const queryParams = new URLSearchParams({
    page: String(filters.page || 0),
    page_size: String(filters.page_size || 50),
    search: filters.search || '',
  })

  if (filters.searchBySKU) {
    queryParams.append('searchBySKU', '1')
  }

  return ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/inventory/warehouse/${
      filters.warehouse_id
    }/grouped?${queryParams.toString()}`,
  }).pipe(catchError(handleError('getGroupedWarehouseInventory')))
}

export const getWarehouseProductVariations = (
  warehouse_id: number | string,
  parent_product_id: number,
) =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/inventory/warehouse/${warehouse_id}/products/${parent_product_id}/variations`,
  }).pipe(catchError(handleError('getWarehouseProductVariations')))

export interface IBatchWarehouseInventoryProduct {
  product_id: number
  count: number
}

export interface IBatchWarehouseInventoryPayload {
  parent_product_id: number
  update_type: 'add' | 'set'
  status: 'on_hand'
  notes: string
  products: IBatchWarehouseInventoryProduct[]
}

export const batchUpdateWarehouseInventory = (
  warehouse_id: number | string,
  data: IBatchWarehouseInventoryPayload,
) =>
  ajax({
    headers: requestHeader(),
    method: 'PUT',
    withCredentials: false,
    url: `${baseURL}api/v2/inventory/warehouse/${warehouse_id}`,
    body: data,
  }).pipe(catchError(handleError('batchUpdateWarehouseInventory')))

export const addNewProducts = (data: any) =>
  ajax({
    headers: requestHeader(true),
    method: 'POST',
    withCredentials: false,
    url: `${baseURL}api/v2/products`,
    body: data,
  }).pipe(catchError(handleError('addNewProducts')))

export const updateProducts = (data) =>
  ajax({
    headers: requestHeader(),
    method: 'PUT',
    withCredentials: false,
    url: `${baseURL}api/v2/products/${data.product_id}`,
    body: data,
  }).pipe(catchError(handleError('updateProducts')))

export const addNewSupplier = (data: any) =>
  ajax({
    headers: requestHeader(),
    method: 'POST',
    withCredentials: false,
    url: `${baseURL}api/v2/suppliers`,
    body: data,
  }).pipe(catchError(handleError('addNewSupplier')))

export const updatePurchaseStatus = (order_id, status) =>
  ajax({
    headers: requestHeader(),
    method: 'PUT',
    withCredentials: false,
    url: `${baseURL}api/v2/purchases/${order_id}/status`,
    body: status,
  }).pipe(catchError(handleError('updatepurchaseStatus')))

export const updatePurchaseDetail = (purchase_id, data) =>
  ajax({
    headers: requestHeader(),
    method: 'PUT',
    withCredentials: false,
    url: `${baseURL}api/v2/purchases/${purchase_id}`,
    body: data,
  }).pipe(catchError(handleError('updatePurchaseDetail')))

export const updateReceiveCount = (data, purchase_id) =>
  ajax({
    headers: requestHeader(),
    method: 'PUT',
    withCredentials: false,
    url: `${baseURL}api/v2/purchases/${purchase_id}/receive`,
    body: data,
  }).pipe(catchError(handleError('updateReceiveCount')))

export const fetchPurchaseStatus = () =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/purchases/status`,
  }).pipe(catchError(handleError('fetchPurchaseStatus')))
export const fetchProductHistory = (
  product_id,
  warehouse_id,
  sortBy?,
  sortOrder?,
) => {
  let url = `${baseURL}api/v2/inventory/${product_id}/warehouse/${warehouse_id}/history`
  if (sortBy) {
    // Use snake_case parameters as expected by the API
    url += `?sort_by=${sortBy}&sort_order=${sortOrder || 'asc'}`
  }

  return ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url,
  }).pipe(catchError(handleError('fetchProductHistory')))
}

export const updateProductInventoryDist = (product_id, data) =>
  ajax({
    headers: requestHeader(),
    method: 'PUT',
    withCredentials: false,
    url: `${baseURL}api/v2/products/${product_id}/inventory`,
    body: data,
  }).pipe(catchError(handleError('updateProductInventoryDist')))

export const addProductVariation = (data: any, product_id) =>
  ajax({
    headers: requestHeader(),
    method: 'POST',
    withCredentials: false,
    url: `${baseURL}api/v2/product/${product_id}/variation`,
    body: data,
  }).pipe(catchError(handleError('addProductVariation')))

export const uploadInventory = (data) => {
  const formData = new FormData()
  formData.append('file', data.inventory_list, data.inventory_list.name)
  formData.append('warehouse_id', data.warehouse_id)
  formData.append('file_name', data.file_name)
  formData.append('update_type', data.update_type)
  formData.append('notes', data.notes)
  return ajax({
    headers: requestHeader(true),
    method: 'POST',
    withCredentials: false,
    url: `${baseURL}api/v2/inventory/bulk/upload`,
    body: formData,
  }).pipe(catchError(handleError('uploadInventory')))
}

export const exportInventory = (filters) => {
  const { token } = JSON.parse(
    localStorage.getItem('user_info') || '{token: null}',
  )
  const downloadLink = document.createElement('a')
  downloadLink.href = `${baseURL}api/v2/export/inventory/${token}?${objToQueryString(
    filters,
  )}`
  document.body.appendChild(downloadLink)
  downloadLink.click()
  document.body.removeChild(downloadLink)
}

export const validateProducts = (data: {
  product_skus: string[]
  store_id: number
}) =>
  ajax({
    headers: requestHeader(),
    method: 'POST',
    withCredentials: false,
    url: `${baseURL}api/v2/product/validate-products`,
    body: data,
  }).pipe(catchError(handleError('validateProducts')))

export const bulkImportPurchases = (
  data:
    | {
        PO_Number: string
        product_sku: string
        product_name: string
        purchase_quantity: number
        received_count: number
        billed_count: number
        damaged_count: number
        estimated_date: string
      }[]
    | {
        supplier_id: number
        warehouse_id: number
        notes?: string
        data: {
          po_number: string
          product_id: number
          purchase_quantity: number
          received_count: number
          billed_count: number
          damaged_count: number
          created_at: string
          expected_at: string
        }[]
      },
) =>
  ajax({
    headers: requestHeader(),
    method: 'POST',
    withCredentials: false,
    url: `${baseURL}api/v2/purchases/bulk/import`,
    body: data,
  }).pipe(catchError(handleError('bulkImportPurchases')))

// Inventory Cycle Management APIs
export const getInventoryCycles = (filters?: {
  warehouse_id?: number
  status?: string
  page?: number
  page_size?: number
}) =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/inventory/cycles?${objToQueryString(filters || {})}`,
  }).pipe(catchError(handleError('getInventoryCycles')))

export const createInventoryCycle = (data: {
  warehouse_id: number
  total_products: number
  total_matched_products_count?: number
  total_unmatched_products_count?: number
  products: Array<{
    product_id: number
    product_sku: string
    warehouse_count: number
    physical_count: number
    discripancy: number
    reason: string
  }>
  comment?: string
  status: string
  approver_id?: number
}) =>
  ajax({
    headers: requestHeader(),
    method: 'POST',
    withCredentials: false,
    url: `${baseURL}api/v2/inventory/cycles`,
    body: data,
  }).pipe(catchError(handleError('createInventoryCycle')))

export const addProductToCycle = (data: {
  warehouse_id: number
  status: string
  product: {
    product_id: number
    product_sku: string
    warehouse_id: number
    physical_count: number
    reason: string
  }
  cycle_id?: number
  approver_id?: number
}) =>
  ajax({
    headers: requestHeader(),
    method: 'POST',
    withCredentials: false,
    url: `${baseURL}api/v2/inventory/cycles`,
    body: data,
  }).pipe(catchError(handleError('addProductToCycle')))

export const updateInventoryCycle = (
  cycleId: number,
  data: {
    total_products: number
    total_matched_products_count?: number
    total_unmatched_products_count?: number
    products: Array<{
      product_id: number
      product_sku: string
      warehouse_count: number
      physical_count: number
      discripancy: number
      reason: string
    }>
    comment?: string
    status?: string
  },
) =>
  ajax({
    headers: requestHeader(),
    method: 'PUT',
    withCredentials: false,
    url: `${baseURL}api/v2/inventory/cycles/${cycleId}`,
    body: data,
  }).pipe(catchError(handleError('updateInventoryCycle')))

export const getInventoryCycleDetails = (cycleId: number) =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/inventory/cycles/${cycleId}/details`,
  }).pipe(catchError(handleError('getInventoryCycleDetails')))

export const updateInventoryCycleDetail = (
  cycleId: number,
  detailId: number,
  data: {
    physical_count: number
    warehouse_id: number | string
    product_id: number | string
    reason_for_discrepancy?: string
  },
) =>
  ajax({
    headers: requestHeader(),
    method: 'PUT',
    withCredentials: false,
    url: `${baseURL}api/v2/inventory/cycles/${cycleId}/details/${detailId}`,
    body: data,
  }).pipe(catchError(handleError('updateInventoryCycleDetail')))

export const approveInventoryCycle = (
  data: { status: string },
  cycleId: number,
) =>
  ajax({
    headers: requestHeader(),
    method: 'PUT',
    withCredentials: false,
    url: `${baseURL}api/v2/inventory/cycles/${cycleId}/update-status`,
    body: data,
  }).pipe(catchError(handleError('approveInventoryCycle')))

export const getWarehouses = () =>
  ajax({
    headers: requestHeader(),
    method: 'GET',
    withCredentials: false,
    url: `${baseURL}api/v2/warehouses`,
  }).pipe(catchError(handleError('getWarehouses')))
